import { CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface ScheduledChangeAlertProps {
  /** Formatted display date when the scheduled change takes effect */
  date: string;
  /** Callback when user clicks "Show this change" */
  onShowChange: () => void;
  className?: string;
}

/**
 * Alert banner displayed when a tab has a future scheduled change.
 * Shows the effective date and provides a button to navigate to the Changes tab
 * with pre-applied filters for the specific change.
 */
export function ScheduledChangeAlert({ date, onShowChange, className }: ScheduledChangeAlertProps) {
  const { t } = useTranslation('common');

  return (
    <Alert variant="warning" className={className}>
      <CalendarClock className="h-5 w-5" />
      <AlertDescription className="flex w-full items-center justify-between gap-4">
        <span>{t('scheduledChange.willTakeEffectOn', { date })}</span>
        <Button
          variant="link"
          size="sm"
          className="h-auto shrink-0 p-0 text-amber-700 underline underline-offset-4 hover:text-amber-900 dark:text-orange-300 dark:hover:text-orange-200"
          onClick={onShowChange}
        >
          {t('scheduledChange.showThisChange')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
